// The retention touch types, in one place, with the one thing every
// consumer kept getting wrong: which activity tab a touch belongs in.
//
// CONSUMER: both (pure TypeScript — no db, no DOM, so the node pool and the
// browser bundle can both import it)
//
// WHY THIS FILE EXISTS (Ryan, 2026-09-17). Ryan logged an in-person meeting
// with a couple, wrote the meeting up in the box, and the next morning could
// not find it. It was never lost — tapping a touch chip writes an `activities`
// row, not a `notes` row — but two separate lists of activity types had never
// learned these seven, so his meeting filed itself under "System" next to the
// automation noise, and the merged Notes & meetings feed could only see four
// of the seven. Two lists, two different holes, one root cause: the touch
// types were defined next to the code that WRITES them and nowhere near the
// code that READS them.
//
// So: the list lives here, the tab decision lives here, and a consumer that
// adds a new touch type gets it everywhere at once. (The eighth, "Texted",
// arrived 2026-09-24 from Brian's ask. It reuses the existing `text_sent`
// activity type, which the conversation rule already counts.)

export const RETENTION_TOUCH_TYPES = [
  "meeting_in_person",
  "meeting_phone",
  "meeting_video",
  "text_sent",
  "gift_sent",
  "card_sent",
  "event_attended",
  "life_event_acknowledged",
] as const;

export type RetentionTouchType = (typeof RETENTION_TOUCH_TYPES)[number];

export function isRetentionTouchType(t: unknown): t is RetentionTouchType {
  return typeof t === "string" && (RETENTION_TOUCH_TYPES as readonly string[]).includes(t);
}

/** The plain-English label written onto `activities.summary` when the touch
 *  is logged, and read back verbatim as the headline everywhere it shows. */
export const TOUCH_SUMMARY: Record<RetentionTouchType, string> = {
  meeting_in_person: "In-person meeting",
  meeting_phone: "Phone conversation",
  meeting_video: "Video conversation",
  text_sent: "Texted",
  gift_sent: "Gift sent",
  card_sent: "Handwritten card sent",
  event_attended: "Attended a Stewardship event",
  life_event_acknowledged: "Life event acknowledged with personal outreach",
};

/** Which activity tab a touch belongs in.
 *
 *  "meeting" — you were with them: in person, on video, or at an event.
 *  "outreach" — you reached out and nobody talked back: a card, a gift, a
 *  note on a life event, a text.
 *
 *  DECISION (2026-09-24): "Texted" is outreach, not a meeting. Unlike a
 *  logged phone conversation, a text doesn't mean anyone talked back. It
 *  still counts as a touch (text_sent is on the conversation allow-list);
 *  this only decides which tab it files under.
 *
 *  DECISION (Grant, 2026-09-17): a logged Phone conversation counts as a
 *  MEETING, even though a calendar-synced `call` activity counts as Comms.
 *  The chip's own hint is "a real conversation, not a voicemail" — an advisor
 *  taps it after actually talking to somebody, which is the same substance as
 *  sitting across from them. The alternative (match `call`, send it to Comms)
 *  was considered and rejected; it is the one place these two lists disagree,
 *  so if a phone touch ever looks misfiled, this is the line to change. */
export function touchTypeChannel(t: RetentionTouchType): "meeting" | "outreach" {
  switch (t) {
    case "meeting_in_person":
    case "meeting_phone":
    case "meeting_video":
    case "event_attended":
      return "meeting";
    case "text_sent":
    case "gift_sent":
    case "card_sent":
    case "life_event_acknowledged":
      return "outreach";
  }
}

/** The touch types that belong in a "Meetings" tab or filter. */
export const MEETING_TOUCH_TYPES: readonly RetentionTouchType[] = RETENTION_TOUCH_TYPES.filter(
  (t) => touchTypeChannel(t) === "meeting",
);

/** The touch types that belong in a "Comms" tab — outbound contact the
 *  client never answered. */
export const OUTREACH_TOUCH_TYPES: readonly RetentionTouchType[] = RETENTION_TOUCH_TYPES.filter(
  (t) => touchTypeChannel(t) === "outreach",
);
